'use client'

import { USE_V2_OFFERS_PREVIEW } from '@/utils/offer-api'
import NatTermsSummary from './nat-terms-summary'
import OfferMessage from './offer-message'

import OfferUsd from './offer-usd'

import { useEffect, useId, useRef, useState } from 'react'
import useSWR from 'swr'
import { Bitcoin, ChevronDown } from 'lucide-react'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import Wallet from '@/utils/wallet'
import { offersEnabled, offerStatusLabel, type Offer } from '@/utils/offers'
import {
	canRefundOffer,
	fetchIncomingOffers,
	formatOfferBtc,
	isActiveOffer,
	offerRefundPending
} from '@/utils/offer-inbox'
import OfferExpiry from './offer-expiry'
import OfferTargetSummary from './offer-target-summary'
import ReviewOfferModal from './review-offer-modal'
import CancelOfferModal from './cancel-offer-modal'
import styles from './offer-dialog.module.css'
import layout from './wallet-offers.module.css'
import Loading from '@/components/circle-loading'
import ScrollLoadMore from '@/components/scroll-load-more'

type Tab = 'received' | 'sent' | 'history'
function OfferManager({ address, walletPage }: { address: string; walletPage: boolean }) {
	const { data, error, isLoading, mutate } = useSWR(
		['offers:incoming', address],
		([, address]) => fetchIncomingOffers(address),
		{ refreshInterval: 30_000, revalidateOnFocus: true }
	)
	const [expanded, setExpanded] = useState(!walletPage)
	const panelId = useId()
	const [tab, setTab] = useState<Tab>('received')
	useEffect(() => {
		const reveal = () => {
			if (window.location.hash === '#offers') setExpanded(true)
			if (window.location.hash === '#offers-sent') {
				setExpanded(true)
				setTab('sent')
				requestAnimationFrame(() => document.getElementById('offers')?.scrollIntoView({ block: 'start' }))
			}
		}
		reveal()
		window.addEventListener('hashchange', reveal)
		return () => window.removeEventListener('hashchange', reveal)
	}, [])
	const [limit, setLimit] = useState(6)
	const [review, setReview] = useState<{
		offer: Offer
		action: 'accept' | 'decline' | 'cancel'
	} | null>(null)
	const [now, setNow] = useState(Date.now)
	const labelId = useId()
	const section = useRef<HTMLElement>(null)
	useEffect(() => {
		const element = section.current
		if (!element) return
		const observer = new IntersectionObserver(
			([entry]) => {
				element.dataset.visible = String(entry.isIntersecting)
			},
			{ threshold: 0.25 }
		)
		observer.observe(element)
		return () => observer.disconnect()
	}, [])
	useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), 1000)
		return () => clearInterval(timer)
	}, [])
	const received = (data?.received || []).filter(
		(o) => o.seller_address === address && o.buyer_address !== address && o.inscription_id
	)
	const sent = (data?.sent || []).filter((o) => o.buyer_address === address)
	const lists = {
		received: received.filter((o) => isActiveOffer(o, now)),
		sent: sent.filter((o) => isActiveOffer(o, now) || ['settling', 'cancelling'].includes(o.state)),
		history: [
			...new Map(
				[...received, ...sent]
					.filter((o) => !isActiveOffer(o, now) && !['settling', 'cancelling'].includes(o.state))
					.map((o) => [o.id, o])
			).values()
		]
	}
	const offers = lists[tab]
		.slice()
		.sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
	const changed = () => {
		// Submission is not confirmation; the API decides the winning state.
		void mutate()
	}

	return (
		<section
			ref={section}
			id="offers"
			aria-labelledby={labelId}
			className={`${styles.profileOffers} ${layout.manager} ${
				walletPage ? `${styles.walletOffers} ${layout.wallet}` : ''
			}`}
		>
			{walletPage && (
				<button
					className={layout.peek}
					type="button"
					aria-expanded={expanded}
					aria-controls={panelId}
					onClick={() => {
						if (!expanded) {
							setTab(lists.received.length ? 'received' : lists.sent.length ? 'sent' : lists.history.length ? 'history' : 'received')
							setLimit(6)
						}
						setExpanded((v) => !v)
					}}
				>
					<span className={layout.peekCopy}>
						<strong>Offers</strong>
						{!expanded && <span>{lists.received.length} received · {lists.sent.length} sent</span>}
					</span>
					<ChevronDown
						size={20}
						className={expanded ? layout.chevronOpen : ''}
						aria-hidden="true"
					/>
				</button>
			)}
			<h2 id={labelId} className="sr-only">
				Offers
			</h2>
			<div id={panelId} hidden={!expanded} className={layout.expanded}>
				{!walletPage && <header className={styles.managerHeader}><h2>Offers</h2></header>}
				<div className={layout.tabs} role="tablist" aria-label="Offer filters">
					{(['received', 'sent', 'history'] as Tab[]).map((value) => (
						<button
							type="button"
							key={value}
							role="tab"
							id={`${panelId}-${value}`}
							aria-selected={tab === value}
							aria-controls={`${panelId}-list`}
							tabIndex={tab === value ? 0 : -1}
							onKeyDown={(e) => {
								const values: Tab[] = ['received', 'sent', 'history']
								const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
								if (!delta && e.key !== 'Home' && e.key !== 'End') return
								e.preventDefault()
								const next =
									values[
										e.key === 'Home'
											? 0
											: e.key === 'End'
											? 2
											: (values.indexOf(value) + delta + 3) % 3
									]
								setTab(next)
								setLimit(6)
								document.getElementById(`${panelId}-${next}`)?.focus()
							}}
							onClick={() => {
								setTab(value)
								setLimit(6)
							}}
						>
							{value === 'received' ? 'Received' : value === 'sent' ? 'Sent' : 'History'}{' '}
							<span>{lists[value].length}</span>
						</button>
					))}
				</div>
				<div role="tabpanel" id={`${panelId}-list`} aria-labelledby={`${panelId}-${tab}`}>
					{isLoading ? (
						<div className={layout.empty} role="status" aria-label="Loading offers"><Loading size={20} /></div>
					) : error ? (
						<p className={styles.error} role="alert">
							Couldn’t load offers. <button type="button" onClick={() => void mutate()}>Retry</button>
						</p>
					) : null}
					{!isLoading && !error && offers.length === 0 && (
						<div className={layout.empty}>
							<strong>
								{tab === 'received'
									? 'No received offers'
									: tab === 'sent'
									? 'No sent offers'
									: 'No history'}
							</strong>

						</div>
					)}
					<div className={layout.list}>
						{offers.slice(0, limit).map((offer) => {
							const own = offer.buyer_address === address
							const active = isActiveOffer(offer, now)
							const pending = own && canRefundOffer(offer) && offerRefundPending(offer.id)
							return (
								<article key={offer.id} className={`${styles.managerCard} ${layout.card}`}>
									<OfferTargetSummary offer={offer} showFloor={!own && active} />
									<div className={styles.managerAmount}>
										<strong>
											<Bitcoin size={17} className={styles.bitcoin} aria-hidden="true" />
											{formatOfferBtc(offer.price_sats)} BTC <OfferUsd sats={offer.price_sats} />
											<NatTermsSummary offer={offer} />
										</strong>
										<span
											className={
												offer.state === 'accepted'
													? layout.accepted
													: offer.state === 'rejected'
													? layout.declined
													: undefined
											}
										>
											{active
												? own
													? 'Sent · Active'
													: 'Received · Active'
												: `${own ? 'Sent' : 'Received'} · ${
														USE_V2_OFFERS_PREVIEW &&
														offer.authorization_version !== 2 &&
														canRefundOffer(offer)
															? 'Refund only'
															: offer.state === 'active'
															? 'Expired'
															: offerStatusLabel(offer)
												  }`}

										</span>
									</div>
									<OfferExpiry offer={offer} now={now} />
									<OfferMessage offer={offer} message={offer.message} />
									{!active && canRefundOffer(offer) && own && (
										<p className={styles.hint}>
											{['settling', 'cancelling'].includes(offer.state)
												? 'Waiting for Bitcoin confirmation. A competing sale or refund may confirm first.'
												: 'Escrow is still funded. Refund it to return your BTC.'}
										</p>
									)}
									{pending && (
										<p className={styles.hint}>
											Refund result unconfirmed. Check your wallet and refresh.
										</p>
									)}
									<div className={styles.rowActions}>
										{!own && active && (
											<>
												<button
													type="button"
													onClick={() => setReview({ offer, action: 'accept' })}
												>
													Accept offer
												</button>
												<button
													type="button"
													onClick={() => setReview({ offer, action: 'decline' })}
												>
													Decline
												</button>
											</>
										)}
										{own && canRefundOffer(offer) && (
											<button
												type="button"
												disabled={pending && offer.state !== 'cancelling'}
												onClick={() => setReview({ offer, action: 'cancel' })}
											>
												{offer.state === 'cancelling'
													? 'Review refund fee'
													: pending
													? 'Refund pending'
													: active
													? 'Cancel offer'
													: 'Refund escrow'}
											</button>
										)}
									</div>
								</article>
							)
						})}
					</div>
					<ScrollLoadMore hasMore={offers.length > limit} itemCount={Math.min(limit, offers.length)} onLoadMore={() => setLimit((n) => n + 6)} />
				</div>
			</div>
			{review &&
				(review.action === 'cancel' ? (
					<CancelOfferModal
						key={review.offer.id}
						offer={review.offer}
						onClose={() => setReview(null)}
						onChanged={changed}
					/>
				) : (
					<ReviewOfferModal
						key={`${review.offer.id}-${review.action}`}
						offer={review.offer}
						itemId={review.offer.inscription_id!}
						action={review.action}
						onClose={() => setReview(null)}
						onChanged={changed}
					/>
				))}
		</section>
	)
}

export default function WalletOffers({ address }: { address?: string }) {
	const loggedIn = useLoggedIn()
	const own = loggedIn ? Wallet.getTaprootAddress() : null
	if (!offersEnabled() || !own || (address && address !== own)) return null
	return <OfferManager key={own} address={own} walletPage={!address} />
}
